import { Breakpoints, CTA, Infobox, Layout, Text, TextTag, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import './SpecialEquipment.scss';
import { Lock } from '@volkswagen-onehub/icons-core';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { LayerHandleV2 } from '@volkswagen-onehub/layer-manager';
import { VehicleObject } from '../../models/Vehicle';
import { DataRequestState } from '../../models/DataRequestState';

const dummyEquipment: string[] = [
	'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
	'In facilisis mauris at nunc semper, efficitur commodo orci sollicitudin',
	'Nam imperdiet urna ut facilisis ultrices',
	'Fusce porta augue et ipsum viverra mattis',
	'Etiam fermentum nisi quis viverra tristique',
	'Mauris malesuada mauris eget varius volutpat',
	'Suspendisse pretium metus at libero sollicitudin varius',
	'Praesent tempor arcu at sodales venenatis',
];

export default function SpecialEquipment(props: Props) {
	const {
		vehicle,
		hasViewPrivateDataRight,
		toggleVehicleVerificationModal,
		technicalDataLoaded,
		trackingService,
		isLoggedIn,
		onClickDisclaimer,
		footnotes,
		vehicleDataIncomplete,
	} = props;
	const { t } = useTranslation('ns1', { useSuspense: false });

	const [specialEquipment, setSpecialEquipment] = useState(hasViewPrivateDataRight ? (vehicle.data.accessoryListSpecial ?? []) : dummyEquipment);
	const [privacyClass, setPrivacyClass] = useState(hasViewPrivateDataRight ? '' : ' blur');

	useEffect(() => {
		if (hasViewPrivateDataRight) {
			setSpecialEquipment(vehicle.data.accessoryListSpecial ?? dummyEquipment);
			setPrivacyClass('');
		} else {
			setSpecialEquipment(dummyEquipment);
			setPrivacyClass(' blur');
		}
	}, [vehicle.data.accessoryListSpecial, hasViewPrivateDataRight]);

	const vinCheckTrackingButton = () => {
		if (trackingService) {
			trackingService.track('VWBasic_Button_Click', {
				eventInfo: {
					eventAction: 'VWBasic_Button_Click',
					pageId: window.location.href,
					pageName: 'Vin Details',
					contentId: 'vinDetails.specialEquipment.unlockButton',
					sectionName: 'specialEquipment',
					brand: 'passenger',
				},
				user: {
					loginStatus: isLoggedIn,
					browserResolutionHeight: window.innerHeight,
					browserResolutionWidth: window.innerWidth,
					browserResolutionOrientation: window.innerHeight >= window.innerWidth ? 'v' : 'h',
				},
				personalization: {
					personalizable: false,
				},
				configuration: {
					CarlineName: vehicle.data.modelCarLine,
					SalesgroupName: vehicle.data.modelSalesGroup,
				},
				link: {
					name: 'toggleVehicleVerificationModal',
				},
			});
		}
	};

	const breakpoints = {
		[Breakpoints.b1600]: [
			{ name: '.', columns: 2 },
			{ name: 'a', columns: 6 },
			{ name: '.', columns: 1 },
			{ name: 'b', columns: 6 },
			{ name: '.', columns: 1 },
			{ name: 'c', columns: 6 },
			{ name: '.', columns: 2 },
		],
		[Breakpoints.b960]: [
			{ name: '.', columns: 2 },
			{ name: 'a', columns: 9 },
			{ name: '.', columns: 2 },
			{ name: 'b', columns: 9 },
			{ name: '.', columns: 2 },
		],
		[Breakpoints.default]: [
			{ name: '.', columns: 2 },
			{ name: 'a', columns: 20 },
			{ name: '.', columns: 2 },
			{ name: '.', columns: 2 },
			{ name: 'b', columns: 20 },
			{ name: '.', columns: 2 },
			{ name: '.', columns: 2 },
			{ name: 'c', columns: 20 },
			{ name: '.', columns: 2 },
		],
	};

	const titleSection = (
		<div className="special-equipment-title">
			<Text className="white" tag={TextTag.h2} appearance={TokenTextAppearance.headline400} bold>
				{t('specialEquipment.title')}{' '}
				<button type="button" className="disclaimer-button" onClick={() => onClickDisclaimer(2)}>
					{footnotes[2].reference}
				</button>
			</Text>
		</div>
	);

	if ((vehicle.state === DataRequestState.Failed || vehicle.state === DataRequestState.Forbidden) && hasViewPrivateDataRight) {
		return (
			<>
				<div className="special-equipment">
					{titleSection}
					<div className="not-found">
						<Infobox hideIcon>
							<Text appearance={TokenTextAppearance.label100}>{t('specialEquipment.notFound')}</Text>
						</Infobox>
					</div>
				</div>
			</>
		);
	}

	return (
		<div className="special-equipment">
			{titleSection}

			{!hasViewPrivateDataRight && (
				<div className="unlock">
					<div className="unlock-icon">
						<Lock aria-hidden="true" />
					</div>

					<CTA
						tag="button"
						emphasis="primary"
						onClick={() => {
							vinCheckTrackingButton();
							toggleVehicleVerificationModal();
						}}
						disabled={vehicleDataIncomplete || technicalDataLoaded !== DataRequestState.Available}
					>
						<Text appearance={TokenTextAppearance.label150}>{t('specialEquipment.unlockButton')}</Text>
					</CTA>
				</div>
			)}

			<div data-testid="special-equipment-content" className={`${privacyClass}`}>
				<Layout appearance={breakpoints}>
					{specialEquipment.map((text) => (
						<div data-testid="special-feature" style={{ color: 'white' }} key={`special-feature-${text}`}>
							{vehicle.state === DataRequestState.Requested ? (
								<Skeleton width={256} />
							) : (
								<Text className="equipment-item white" appearance={TokenTextAppearance.copy200}>
									{text}
								</Text>
							)}
						</div>
					))}
				</Layout>
			</div>
		</div>
	);
}

interface Props {
	hasViewPrivateDataRight: boolean;
	vehicleDataIncomplete: boolean;
	vehicle: VehicleObject;
	toggleVehicleVerificationModal: () => void;
	technicalDataLoaded: DataRequestState;
	trackingService: any;
	isLoggedIn: boolean;
	onClickDisclaimer: (disclaimerNumber: number) => void;
	footnotes: { reference: string; text: string; handle: LayerHandleV2<any> }[];
}
